<template>
  <div class="production-devices-table-container">
    <DataTable
      :value="flatDevices"
      v-model:filters="filters"
      dataKey="id"
      filterDisplay="row"
      :globalFilterFields="[
        'company_name',
        'device_name',
        'device_eic',
        'technology',
      ]"
      class="border rounded"
      :scrollable="flatDevices.length > 14"
      :scrollHeight="flatDevices.length > 14 ? '600px' : undefined"
      :virtualScrollerOptions="
        flatDevices.length > 14
          ? { itemSize: 42, numToleratedItems: 20, autoSize: true }
          : undefined
      "
      sortField="company_name"
      :sortOrder="1"
    >
      <template #empty>
        <div class="text-center py-4 text-muted">
          {{ t("dashboard.consolidated.production_devices_table.no_devices") }}
        </div>
      </template>

      <!-- Company Name -->
      <Column
        field="company_name"
        :header="
          t('dashboard.consolidated.production_devices_table.company_name')
        "
        sortable
        :showFilterMatchModes="false"
        :showFilterMenu="false"
        headerStyle="width: 10rem"
        class="wrap-text"
      >
        <template #sorticon="{ sortOrder: so }">
          <font-awesome-icon
            :icon="getSortIcon(so)"
            class="pv-datatable-sort-icon"
          />
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <MultiSelect
            v-model="filterModel.value"
            :options="uniqueCompanies"
            optionLabel="label"
            optionValue="value"
            :placeholder="t('common.filter')"
            class="p-column-filter w-100"
            filter
            @change="filterCallback()"
            @selectall-change="filterCallback()"
          >
            <template #filtericon>
              <font-awesome-icon
                icon="search"
                class="text-muted pv-multiselect-filter-icon"
              />
            </template>
            <template #value="{ value, placeholder }">
              <span v-if="value && value.length" class="text-primary fw-bold">
                {{ t("dashboard.portfolio.table.filters.active") }}
              </span>
              <span v-else class="text-muted">{{ placeholder }}</span>
            </template>
          </MultiSelect>
        </template>
      </Column>

      <!-- Technology -->
      <Column
        field="technology"
        :header="
          t('dashboard.consolidated.production_devices_table.technology')
        "
        sortable
        :showFilterMatchModes="false"
        :showFilterMenu="false"
        headerStyle="width: 7rem"
      >
        <template #sorticon="{ sortOrder: so }">
          <font-awesome-icon
            :icon="getSortIcon(so)"
            class="pv-datatable-sort-icon"
          />
        </template>
        <template #body="{ data }">
          <div class="d-flex align-items-center">
            <TechnologyIcon :technology="data.technology" class="me-2" />
            {{ translateTechnology(data.technology, t) }}
          </div>
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <MultiSelect
            v-model="filterModel.value"
            :options="uniqueTechnologies"
            optionLabel="label"
            optionValue="value"
            :placeholder="t('common.filter')"
            class="p-column-filter w-100"
            @change="filterCallback()"
            @selectall-change="filterCallback()"
          >
            <template #filtericon>
              <font-awesome-icon
                icon="search"
                class="text-muted pv-multiselect-filter-icon"
              />
            </template>
            <template #value="{ value, placeholder }">
              <span v-if="value && value.length" class="text-primary fw-bold">
                {{ t("dashboard.portfolio.table.filters.active") }}
              </span>
              <span v-else>{{ placeholder }}</span>
            </template>
          </MultiSelect>
        </template>
      </Column>

      <!-- Device EIC -->
      <Column
        field="device_eic"
        :header="t('dashboard.consolidated.production_devices_table.device_id')"
        sortable
        :showFilterMatchModes="false"
        :showFilterMenu="false"
        headerStyle="width: 9rem"
      >
        <template #sorticon="{ sortOrder: so }">
          <font-awesome-icon
            :icon="getSortIcon(so)"
            class="pv-datatable-sort-icon"
          />
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <InputText
            v-model="filterModel.value"
            type="text"
            @input="filterCallback()"
            class="p-column-filter w-100"
            :placeholder="t('common.filter')"
          />
        </template>
      </Column>

      <!-- Device Name -->
      <Column
        field="device_name"
        :header="
          t('dashboard.consolidated.production_devices_table.device_name')
        "
        sortable
        :showFilterMatchModes="false"
        :showFilterMenu="false"
        headerStyle="width: 12rem"
        class="wrap-text"
      >
        <template #body="{ data }">
          {{ data.device_name }}
          <font-awesome-icon
            v-if="showScheduledTransactions && data.has_scheduled_transactions"
            icon="fa-solid fa-circle-info"
            class="ms-1 text-info"
            :title="
              t(
                'dashboard.consolidated.production_devices_table.scheduled_transactions_tooltip'
              )
            "
          />
        </template>
        <template #sorticon="{ sortOrder: so }">
          <font-awesome-icon
            :icon="getSortIcon(so)"
            class="pv-datatable-sort-icon"
          />
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <InputText
            v-model="filterModel.value"
            type="text"
            @input="filterCallback()"
            class="p-column-filter w-100"
            :placeholder="t('common.filter')"
          />
        </template>
      </Column>

      <!-- Address -->
      <Column
        field="address"
        :header="t('registry_device.address')"
        sortable
        :showFilterMatchModes="false"
        :showFilterMenu="false"
        headerStyle="width: 9rem"
        class="wrap-text"
      >
        <template #sorticon="{ sortOrder: so }">
          <font-awesome-icon
            :icon="getSortIcon(so)"
            class="pv-datatable-sort-icon"
          />
        </template>
        <template #body="{ data }">
          {{ data.address ?? "-" }}
        </template>
      </Column>

      <!-- Capacity (kW) -->
      <Column
        field="device_power"
        :header="`${t('dashboard.consolidated.production_devices_table.capacity')} (${t('common.kw')})`"
        sortable
        dataType="numeric"
        :showFilterMatchModes="false"
        :showFilterMenu="false"
        headerStyle="width: 6rem"
      >
        <template #sorticon="{ sortOrder: so }">
          <font-awesome-icon
            :icon="getSortIcon(so)"
            class="pv-datatable-sort-icon"
          />
        </template>
        <template #body="{ data }">
          <div class="text-end fw-semibold font-variant-numeric-tabular">
            {{ data.device_power?.toLocaleString() }}
          </div>
        </template>
      </Column>

      <!-- COD -->
      <Column
        field="commercial_operation_date"
        :header="t('registry_device.commercial_operation_date')"
        sortable
        :showFilterMatchModes="false"
        :showFilterMenu="false"
        headerStyle="width: 7rem"
      >
        <template #sorticon="{ sortOrder: so }">
          <font-awesome-icon
            :icon="getSortIcon(so)"
            class="pv-datatable-sort-icon"
          />
        </template>
        <template #body="{ data }">
          {{
            data.commercial_operation_date
              ? formatDateTimeIntoDate(data.commercial_operation_date)
              : "-"
          }}
        </template>
      </Column>

      <!-- Supported -->
      <Column
        field="support"
        :header="t('registry_device.supported_device')"
        sortable
        :showFilterMatchModes="false"
        :showFilterMenu="false"
        headerStyle="width: 6rem"
      >
        <template #sorticon="{ sortOrder: so }">
          <font-awesome-icon
            :icon="getSortIcon(so)"
            class="pv-datatable-sort-icon"
          />
        </template>
        <template #body="{ data }">
          <span
            class="badge rounded-pill badge-compact"
            :class="
              data.support
                ? 'bg-warning-subtle text-warning-emphasis'
                : 'bg-success-subtle text-success-emphasis'
            "
          >
            {{ data.support ? t("common.yes") : t("common.no") }}
          </span>
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <MultiSelect
            v-model="filterModel.value"
            :options="supportOptions"
            optionLabel="label"
            optionValue="value"
            :placeholder="t('common.filter')"
            class="p-column-filter w-100"
            @change="filterCallback()"
            @selectall-change="filterCallback()"
          >
            <template #filtericon>
              <font-awesome-icon
                icon="search"
                class="text-muted pv-multiselect-filter-icon"
              />
            </template>
            <template #value="{ value, placeholder }">
              <span v-if="value && value.length" class="text-primary fw-bold">
                {{ t("dashboard.portfolio.table.filters.active") }}
              </span>
              <span v-else>{{ placeholder }}</span>
            </template>
          </MultiSelect>
        </template>
      </Column>

      <!-- Ecolabel -->
      <Column
        field="ecolabel_approved"
        :header="t('registry_device.ecolabel_approved')"
        sortable
        :showFilterMatchModes="false"
        :showFilterMenu="false"
        headerStyle="width: 6rem"
      >
        <template #sorticon="{ sortOrder: so }">
          <font-awesome-icon
            :icon="getSortIcon(so)"
            class="pv-datatable-sort-icon"
          />
        </template>
        <template #body="{ data }">
          <span v-if="data.ecolabel_approved !== null">
            <span
              class="badge rounded-pill badge-compact"
              :class="
                data.ecolabel_approved
                  ? 'bg-success-subtle text-success-emphasis'
                  : 'bg-secondary-subtle text-secondary-emphasis'
              "
            >
              {{ data.ecolabel_approved ? t("common.yes") : t("common.no") }}
            </span>
          </span>
          <span v-else class="text-muted">-</span>
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <MultiSelect
            v-model="filterModel.value"
            :options="ecolabelOptions"
            optionLabel="label"
            optionValue="value"
            :placeholder="t('common.filter')"
            class="p-column-filter w-100"
            @change="filterCallback()"
            @selectall-change="filterCallback()"
          >
            <template #filtericon>
              <font-awesome-icon
                icon="search"
                class="text-muted pv-multiselect-filter-icon"
              />
            </template>
            <template #value="{ value, placeholder }">
              <span v-if="value && value.length" class="text-primary fw-bold">
                {{ t("dashboard.portfolio.table.filters.active") }}
              </span>
              <span v-else>{{ placeholder }}</span>
            </template>
          </MultiSelect>
        </template>
      </Column>

      <!-- LT Reporting Status (Lithuanian companies only) -->
      <Column
        v-if="showLtReportingStatus"
        field="lt_reporting_status"
        :header="t('dashboard.automaticReporting.title')"
        :showFilterMatchModes="false"
        :showFilterMenu="false"
        headerStyle="width: 8rem"
      >
        <template #body="{ data }">
          <span
            v-if="data.lt_reporting_status"
            class="badge rounded-pill badge-compact"
            :class="getLtStatusBadgeClass(data.lt_reporting_status)"
          >
            {{ getLtStatusLabel(data.lt_reporting_status) }}
          </span>
          <span v-else class="text-muted">-</span>
        </template>
      </Column>

      <!-- Actions -->
      <Column
        headerStyle="width: 3.5rem"
        :showFilterMatchModes="false"
        :showFilterMenu="false"
      >
        <template #body="{ data }">
          <button
            type="button"
            class="btn btn-sm btn-outline-secondary btn-icon"
            :title="
              t(
                'dashboard.consolidated.production_devices_table.view_projection'
              )
            "
            :aria-label="
              t(
                'dashboard.consolidated.production_devices_table.view_projection'
              )
            "
            @click="openProjectionModal(data)"
          >
            <font-awesome-icon icon="chart-column" />
          </button>
        </template>
      </Column>
    </DataTable>

    <ProductionEstimateOverrideModal
      ref="projectionModalRef"
      :companyId="activeCompanyId"
    />
  </div>
</template>

<script setup lang="ts">
import TechnologyIcon from "@/components/common/TechnologyIcon.vue";
import { formatDateTimeIntoDate } from "@/composables/formatUtils";
import ProductionEstimateOverrideModal from "@/dashboard/production_strategy/ProductionEstimateOverrideModal.vue";
import { Company, RegistryDevice } from "@/types";
import { FilterMatchMode } from "@primevue/core/api";
import Column from "primevue/column";
import DataTable from "primevue/datatable";
import InputText from "primevue/inputtext";
import MultiSelect from "primevue/multiselect";
import { computed, nextTick, ref } from "vue";
import { useI18n } from "vue-i18n";
import { getSortIcon } from "@/utils/sortIcons";
import { translateTechnology } from "@/utils/technologies";

export type LtReportingStatusMap = Map<
  number,
  "missing_association" | "declared" | "pending_approval" | "rejected"
>;

const props = withDefaults(
  defineProps<{
    companies: (Company & { id: number })[];
    registryDevices: RegistryDevice[];
    companyId?: number;
    ltReportingStatuses?: LtReportingStatusMap;
    // Show an info icon next to devices that have registry scheduled transactions configured.
    showScheduledTransactions?: boolean;
  }>(),
  {
    companyId: undefined,
    ltReportingStatuses: undefined,
    showScheduledTransactions: false,
  }
);

const { t } = useI18n();

const projectionModalRef = ref<InstanceType<
  typeof ProductionEstimateOverrideModal
> | null>(null);

const activeCompanyId = ref(props.companyId ?? props.companies[0]?.id ?? 0);

const openProjectionModal = async (device: FlatDevice) => {
  activeCompanyId.value = props.companyId ?? device.company_id;
  await nextTick();
  projectionModalRef.value?.show(device as unknown as RegistryDevice);
};

const showLtReportingStatus = computed(
  () => props.ltReportingStatuses != null && props.ltReportingStatuses.size > 0
);

const filters = ref({
  company_name: { value: [], matchMode: FilterMatchMode.IN },
  technology: { value: [], matchMode: FilterMatchMode.IN },
  device_eic: { value: null, matchMode: FilterMatchMode.CONTAINS },
  device_name: { value: null, matchMode: FilterMatchMode.CONTAINS },
  support: { value: [], matchMode: FilterMatchMode.IN },
  ecolabel_approved: { value: [], matchMode: FilterMatchMode.IN },
});

export interface FlatDevice extends Omit<RegistryDevice, "support"> {
  company_name: string;
  support: boolean;
  lt_reporting_status: string | null;
}

const flatDevices = computed<FlatDevice[]>(() => {
  const companyMap = new Map(props.companies.map((c) => [c.id, c.name ?? ""]));
  return props.registryDevices.map((device) => ({
    ...device,
    company_name: companyMap.get(device.company_id) ?? "",
    support:
      (device.support as unknown) === true ||
      String(device.support).toLowerCase() === "true",
    lt_reporting_status: props.ltReportingStatuses?.get(device.id) ?? null,
  }));
});

const uniqueCompanies = computed(() =>
  [...new Set(flatDevices.value.map((d) => d.company_name))]
    .filter(Boolean)
    .sort()
    .map((name) => ({ label: name, value: name }))
);

const uniqueTechnologies = computed(() =>
  [...new Set(flatDevices.value.map((d) => d.technology))]
    .filter(Boolean)
    .sort()
    .map((tech) => ({
      label: translateTechnology(tech, t),
      value: tech,
    }))
);

const supportOptions = computed(() => [
  { label: t("common.yes"), value: true },
  { label: t("common.no"), value: false },
]);

const ecolabelOptions = computed(() => [
  { label: t("common.yes"), value: true },
  { label: t("common.no"), value: false },
]);

const getLtStatusBadgeClass = (status: string) => {
  if (status === "declared") return "bg-success-subtle text-success-emphasis";
  if (status === "pending_approval")
    return "bg-warning-subtle text-warning-emphasis";
  if (status === "rejected") return "bg-danger-subtle text-danger-emphasis";
  return "bg-warning-subtle text-warning-emphasis";
};

const getLtStatusLabel = (status: string) => {
  if (status === "declared")
    return t("dashboard.automaticReporting.automaticallyDeclared");
  if (status === "pending_approval")
    return t("dashboard.automaticReporting.pendingApproval");
  if (status === "rejected") return t("common.rejected");
  return t("dashboard.automaticReporting.missingAssociation");
};
</script>

<style scoped lang="scss">
.production-devices-table-container {
  overflow-x: hidden;
}

.badge-compact {
  font-size: 0.7rem;
  padding: 0.25rem 0.5rem;
  font-weight: 600;
}

.font-variant-numeric-tabular {
  font-variant-numeric: tabular-nums;
  font-feature-settings: "tnum";
}

.btn-icon {
  width: 28px;
  height: 28px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
</style>
